/* eslint-disable i18next/no-literal-string */
import React, { useEffect, useMemo, useState } from 'react';

import { resolveBackendResourceUrl } from '../../config/environment';
import api from '../../services/api';
import { ResearchConfig, ResearchVideo } from '../../types/study';
import { classifyVideoSource, VideoSource } from '../../utils/videoSources';
import { YouTubePlayer } from '../shared/YouTubePlayer';

type SourceMode = 'file' | 'link';
type Draft = {
  id?: number; title: string; mode: SourceMode; url: string; file: File | null;
  duration: number | null; rights: boolean; active: boolean; order: number;
};

interface Props {
  config: ResearchConfig;
  busy: boolean;
  run(action: { (): Promise<void> }): void;
  onConfig: (config: ResearchConfig) => void;
  onError: (message: string) => void;
  onNotice: (message: string) => void;
}

const COPY = {
  libraryTitle: 'Biblioteca de estímulos',
  libraryHelp: 'Añade tantos videos como requiera el protocolo. Las nuevas sesiones se reparten entre los activos con menor número de asignaciones.',
  addVideo: 'Añadir video',
  assignments: 'asignaciones',
  edit: 'Editar',
  remove: 'Eliminar',
  empty: 'No hay videos. El estudio no puede recibir participantes hasta activar al menos uno.',
  internalTitle: 'Título interno',
  order: 'Orden',
  link: 'Enlace',
  fileMode: 'Archivo MP4/WebM',
  historyLock: 'Este estímulo ya tiene asignaciones. Su fuente y duración forman parte del historial; archívalo y crea otro para sustituirlo.',
  linkLabel: 'Enlace de YouTube o video directo',
  fileLabel: 'Archivo de video',
  keepFile: 'Conservas el archivo actual. Selecciona otro solo si deseas reemplazar la fuente.',
  rights: 'Confirmo que el estudio tiene autorización para utilizar este video.',
  active: 'Activo para nuevas asignaciones',
  cancel: 'Cancelar',
  deleteHelp: 'Solo se puede eliminar si nunca fue asignado. Si tiene historial, archívalo editando su estado.',
};

const emptyDraft = (order: number): Draft => ({
  title: '', mode: 'link', url: '', file: null, duration: null,
  rights: false, active: false, order,
});

const editDraft = (video: ResearchVideo): Draft => ({
  id: video.id, title: video.title,
  mode: video.source_type === 'uploaded' ? 'file' : 'link',
  url: video.url, file: null, duration: video.duration_seconds,
  rights: video.rights_confirmed, active: video.active, order: video.order,
});

const sourceLabel = (video: ResearchVideo) => video.source_type === 'youtube'
  ? 'YouTube'
  : video.source_type === 'uploaded' ? 'Archivo subido' : 'Enlace directo';

export default function StudyVideoAdmin({ config, busy, run, onConfig, onError, onNotice }: Props) {
  const videos: ResearchVideo[] = config.videos || [{
    id: config.video.id || 0,
    title: config.video.title || 'Video del estudio',
    url: config.video.url,
    preview_url: config.video.preview_url || config.video.url,
    duration_seconds: Number(config.video.duration_seconds || 0),
    duration_verified: config.video.duration_verified,
    duration_verified_at: config.video.duration_verified_at,
    source_type: config.video.source_type || 'direct',
    youtube_video_id: config.video.youtube_video_id,
    embed_url: config.video.embed_url,
    rights_confirmed: config.video.rights_confirmed,
    configured: config.video.configured,
    active: config.video.active ?? config.active,
    order: config.video.order || 0,
    assignment_count: config.video.assignment_count || 0,
  }];
  const [draft, setDraft] = useState<Draft | null>(null);
  const [fileUrl, setFileUrl] = useState('');
  const [validationMessage, setValidationMessage] = useState('');
  const [pendingDelete, setPendingDelete] = useState<ResearchVideo | null>(null);
  const candidate = useMemo(
    () => classifyVideoSource(draft?.mode === 'file' ? fileUrl : draft?.url || ''),
    [draft?.mode, draft?.url, fileUrl],
  );
  const editedVideo = videos.find(video => video.id === draft?.id);
  const identityLocked = Boolean(editedVideo?.assignment_count);
  const activeVideos = videos.filter(video => video.active);

  useEffect(() => () => { if (fileUrl) URL.revokeObjectURL(fileUrl); }, [fileUrl]);

  const reload = async () => onConfig((await api.getResearchConfig()).data);
  const openDraft = (next: Draft) => {
    if (fileUrl) URL.revokeObjectURL(fileUrl);
    setFileUrl('');
    setValidationMessage('');
    setDraft(next);
  };

  const chooseFile = (file: File | null) => {
    if (!draft) return;
    if (fileUrl) URL.revokeObjectURL(fileUrl);
    const nextUrl = file ? URL.createObjectURL(file) : '';
    setFileUrl(nextUrl);
    setDraft({ ...draft, file, duration: null });
    setValidationMessage(file ? 'Comprobando duración y reproducción…' : '');
  };

  const acceptDuration = (duration: number) => {
    if (!draft || !Number.isFinite(duration) || duration <= 0 || duration > 3600) {
      setValidationMessage('No se pudo confirmar una duración válida de hasta 60 minutos.');
      return;
    }
    setDraft({ ...draft, duration });
    setValidationMessage(`Vista previa validada: ${duration.toFixed(1)} segundos.`);
  };

  const preview = (source: VideoSource, onValidate?: (duration: number) => void) => {
    if (source.kind === 'youtube') {
      return <YouTubePlayer videoId={source.videoId} ariaLabel="Vista previa del estímulo" testId="video-library-preview-youtube" onReady={onValidate} onError={onError} />;
    }
    if (source.kind === 'direct') {
      return <video data-testid="video-library-preview-direct" src={source.url} controls preload="metadata" aria-label="Vista previa del estímulo" onLoadedMetadata={onValidate ? event => onValidate(event.currentTarget.duration) : undefined} onError={() => onError('No se pudo reproducir esta fuente de video.')} />;
    }
    return null;
  };

  const save = (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft) return;
    const original = editedVideo;
    const sourceChanged = Boolean(draft.file || !draft.id || (
      draft.mode === 'link' && original?.url !== draft.url.trim()
    ) || (draft.mode === 'link' && original && draft.duration !== original.duration_seconds));
    if (sourceChanged && (!draft.rights || !draft.duration)) {
      onError('Confirma los derechos y valida la vista previa antes de guardar.');
      return;
    }
    run(async () => {
      let payload: FormData | Record<string, unknown>;
      if (draft.file) {
        const form = new FormData();
        form.append('file', draft.file);
        form.append('title', draft.title);
        form.append('rights_confirmed', String(draft.rights));
        form.append('active', String(draft.active));
        form.append('order', String(draft.order));
        payload = form;
      } else {
        payload = {
          title: draft.title, rights_confirmed: draft.rights,
          active: draft.active, order: draft.order,
          ...(sourceChanged ? {
            url: draft.url.trim(), duration_seconds: draft.duration, duration_verified: true,
          } : {}),
        };
      }
      await api.saveResearchVideo(payload, draft.id);
      await reload();
      setDraft(null);
      setFileUrl('');
      setValidationMessage('');
      onNotice(`Video ${draft.id ? 'actualizado' : 'añadido'}: ${draft.title}.`);
    });
  };

  const remove = (video: ResearchVideo) => run(async () => {
    await api.deleteResearchVideo(video.id);
    await reload();
    setPendingDelete(null);
    onNotice(`Video eliminado: ${video.title}.`);
  });

  return <section className="rd-panel ra-video-library" data-testid="study-video-admin">
    <div className="ra-library-heading">
      <div><h2>{COPY.libraryTitle}</h2><p>{COPY.libraryHelp}</p></div>
      <button disabled={busy} onClick={() => openDraft(emptyDraft(videos.length))}>{COPY.addVideo}</button>
    </div>

    <div className="ra-video-inventory" role="list" aria-label="Videos del estudio">
      {videos.map(video => <article key={video.id} role="listitem" className={video.active ? 'is-active' : 'is-archived'}>
        <div className="ra-video-order" aria-label={`Orden ${video.order}`}>{video.order}</div>
        <div className="ra-video-summary">
          <div><strong>{video.title}</strong><span>{video.active ? 'Activo' : 'Archivado'}</span></div>
          <p>{sourceLabel(video)} · {video.duration_seconds.toFixed(1)} s · <b>{video.assignment_count}</b> {COPY.assignments}</p>
        </div>
        <div className="ra-video-actions">
          <button disabled={busy} aria-label={`Editar video ${video.title}`} onClick={() => openDraft(editDraft(video))}>{COPY.edit}</button>
          <button disabled={busy} className="ra-button-danger" aria-label={`Eliminar video ${video.title}`} onClick={() => setPendingDelete(video)}>{COPY.remove}</button>
        </div>
      </article>)}
      {!videos.length && <p>{COPY.empty}</p>}
    </div>

    {draft && <form data-testid="video-library-form" aria-busy={busy} onSubmit={save}>
      <fieldset disabled={busy}>
        <legend>{draft.id ? `Editar ${draft.title}` : 'Añadir estímulo'}</legend>
        <div className="ra-columns">
          <label>{COPY.internalTitle}<input required value={draft.title} onChange={event => setDraft({ ...draft, title: event.target.value })} /></label>
          <label>{COPY.order}<input required type="number" min="0" value={draft.order} onChange={event => setDraft({ ...draft, order: Number(event.target.value) })} /></label>
        </div>
        <div className="ra-source-choice" role="radiogroup" aria-label="Tipo de fuente">
          <label><input type="radio" disabled={identityLocked} checked={draft.mode === 'link'} onChange={() => setDraft({ ...draft, mode: 'link', file: null, duration: draft.id ? draft.duration : null })} />{COPY.link}</label>
          <label><input type="radio" disabled={identityLocked} checked={draft.mode === 'file'} onChange={() => setDraft({ ...draft, mode: 'file', url: '', duration: draft.id ? draft.duration : null })} />{COPY.fileMode}</label>
        </div>
        {identityLocked && <p className="ra-history-lock" role="note">{COPY.historyLock}</p>}
        {draft.mode === 'link' ? <>
          <label>{COPY.linkLabel}<input type="text" required disabled={identityLocked} value={draft.url} onChange={event => { setDraft({ ...draft, url: event.target.value, duration: null }); setValidationMessage(''); }} /></label>
          {candidate.kind !== 'invalid' && <div className="ra-video ra-video--draft">{preview(candidate, identityLocked ? undefined : acceptDuration)}</div>}
        </> : <>
          <label>{COPY.fileLabel}<input type="file" disabled={identityLocked} accept="video/mp4,video/webm,.mp4,.webm" required={!draft.id} onChange={event => chooseFile(event.target.files?.[0] || null)} /></label>
          {fileUrl && <div className="ra-video ra-video--draft">{preview(classifyVideoSource(fileUrl), acceptDuration)}</div>}
          {draft.id && !draft.file && !identityLocked && <p>{COPY.keepFile}</p>}
        </>}
        {validationMessage && <p role="status" className={draft.duration ? 'ra-validation-ok' : 'ra-error'}>{validationMessage}</p>}
        <label className="ra-check"><input type="checkbox" checked={draft.rights} onChange={event => setDraft({ ...draft, rights: event.target.checked })} />{COPY.rights}</label>
        <label className="ra-check"><input type="checkbox" checked={draft.active} onChange={event => setDraft({ ...draft, active: event.target.checked })} />{COPY.active}</label>
        <div className="ra-actions"><button type="submit">{busy ? 'Guardando video…' : 'Guardar video'}</button><button type="button" onClick={() => setDraft(null)}>{COPY.cancel}</button></div>
      </fieldset>
    </form>}

    {pendingDelete && <section className="ra-delete-confirmation" role="alertdialog" aria-modal="true" aria-labelledby="delete-video-title">
      <h3 id="delete-video-title">Eliminar “{pendingDelete.title}”</h3>
      <p>{COPY.deleteHelp}</p>
      <div className="ra-actions"><button className="ra-button-danger" disabled={busy} onClick={() => remove(pendingDelete)}>{busy ? 'Eliminando…' : 'Eliminar definitivamente'}</button><button disabled={busy} onClick={() => setPendingDelete(null)}>{COPY.cancel}</button></div>
    </section>}

    {activeVideos.map(video => (
      <details key={`preview-${video.id}`} className="ra-video-preview"><summary>{`Vista previa: ${video.title}`}</summary><div className="ra-video">{preview(classifyVideoSource(video.source_type === 'uploaded' ? resolveBackendResourceUrl(video.preview_url) : video.url))}</div></details>
    ))}
  </section>;
}
